import { z } from 'zod';
import {
  PositiveDecimalString,
  NonNegativeDecimalString,
  PaginationQuerySchema,
} from './common.js';

/**
 * EP pricing tier schemas — legacy `ep_price_tier` / `user.Proportion/index`.
 *
 * The operator maintains a small table mapping CNY/USDT forex bands to the
 * USDT price per 1 EP and the USDT amount per EP trading unit used by the EP
 * P2P market. When a user opens a sell ticket, the backend looks up the
 * tier where `cny_min <= exchange_rate <= cny_max AND is_active` and stamps
 * the ticket with that tier's price + unit amount.
 *
 * The `exchange_rate` itself is a runtime parameter (legacy lived in the
 * `config_info` table as `exchange_rate`). The lookup endpoint accepts it as
 * a query param, so off-platform feeds or manual dashboards can resolve the
 * active tier without having to write back to the database first.
 *
 * Overlapping active tier intervals are rejected at write-time by a
 * service-layer interval check (not a DB exclusion constraint so bulk
 * reseeds stay simple). Tiers never hard-delete — `is_active=false` is the
 * retirement switch, preserving id references from historical tickets.
 */

// ---- Admin CRUD -------------------------------------------------------------

export const EpPriceTierCreateSchema = z
  .object({
    /**
     * Display rank, 1..N. Unique across the table. Not a PK so rows can be
     * inserted or retired without renumbering everything.
     */
    level: z.coerce.number().int().min(1).max(999),
    /** Inclusive lower bound on CNY/USDT forex rate, > 0. */
    cny_min: PositiveDecimalString,
    /** Inclusive upper bound on CNY/USDT forex rate, > 0. Must be >= cny_min. */
    cny_max: PositiveDecimalString,
    /** USDT price per 1 EP, > 0. */
    usdt_price: PositiveDecimalString,
    /** USDT amount per EP trading unit, >= 0 (0 = no unit quantisation). */
    usdt_amount: NonNegativeDecimalString,
    is_active: z.coerce.boolean().default(true),
    note: z.string().max(200).optional(),
  })
  .refine(
    (v) => {
      // Cheap string-based band sanity check. The service also re-validates
      // with Decimal arithmetic before the insert, so a user that slips past
      // this check (e.g. different fractional digit counts tricking the
      // compare) still gets a clean 400.
      const lo = Number(v.cny_min);
      const hi = Number(v.cny_max);
      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return true;
      return lo <= hi;
    },
    { message: 'cny_min must be <= cny_max', path: ['cny_max'] },
  );
export type EpPriceTierCreateRequest = z.infer<typeof EpPriceTierCreateSchema>;

/**
 * Patch schema — re-uses the create shape as `.partial()` but strips the
 * outer `refine` because partial updates may set only one band edge. The
 * service re-runs the full band check after merging the patch onto the
 * current row, so the invariant is still guarded.
 */
export const EpPriceTierPatchSchema = z.object({
  level: z.coerce.number().int().min(1).max(999).optional(),
  cny_min: PositiveDecimalString.optional(),
  cny_max: PositiveDecimalString.optional(),
  usdt_price: PositiveDecimalString.optional(),
  usdt_amount: NonNegativeDecimalString.optional(),
  is_active: z.coerce.boolean().optional(),
  note: z.string().max(200).optional(),
});
export type EpPriceTierPatchRequest = z.infer<typeof EpPriceTierPatchSchema>;

export const EpPriceTierListQuerySchema = PaginationQuerySchema.extend({
  is_active: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export type EpPriceTierListQuery = z.infer<typeof EpPriceTierListQuerySchema>;

// ---- Active-tier lookup -----------------------------------------------------

/**
 * Lookup query for `getActiveTier`. Accepts the current CNY/USDT rate as a
 * query param so off-platform dashboards can resolve the active tier without
 * having to persist the rate first.
 */
export const EpPriceTierActiveQuerySchema = z.object({
  /** When omitted, backend uses `ep.usdt_cny_rate` (default 7.25). */
  exchange_rate: PositiveDecimalString.optional(),
});
export type EpPriceTierActiveQuery = z.infer<typeof EpPriceTierActiveQuerySchema>;

// ---- Bulk reseed ------------------------------------------------------------

/**
 * Bulk reseed payload — replaces the entire tier table in one serializable
 * transaction. Used by the admin "restore legacy 20 tiers" button and by the
 * seed script. The service enforces band non-overlap across the whole batch.
 */
export const EpPriceTierBulkReplaceSchema = z.object({
  tiers: z
    .array(EpPriceTierCreateSchema)
    .min(1, 'must supply at least one tier')
    .max(100, 'batch limited to 100 tiers per reseed'),
});
export type EpPriceTierBulkReplaceRequest = z.infer<typeof EpPriceTierBulkReplaceSchema>;
